import React, { useEffect, useMemo, useState } from 'react';
import Badge from '../../../components/badge/Badge';
import Card from '../../../components/card/Card';
import Collapsable from '../../../components/collapsable/Collapsable';
import Table from '../../../components/table/table';
import P from '../../../components/texts/p/P';
import H from '../../../components/texts/h/H';
import Span from '../../../components/texts/span/Span';
import type { NosisRejectedChecksDetail } from '../../../types/interfaces';
import { colors } from '../../../styles/_colors';
import theme from '../../../styles/theme';
import {
  buildAntecedentesBlocks,
  buildMonthlyTotalActual,
  buildRedNosisMetrics,
  ensurePjReasonRows,
  formatReasonLabel,
  formatFineRow,
  mapCheckRowsForTable,
  mapMonthlyRows,
  PJ_BAND_KEYS,
  PJ_BAND_LABELS,
  pjBandCell,
  pjTotalsCell,
  type PjBandKey,
} from './nosisRejectedChecksFormat';

type Props = {
  detail: NosisRejectedChecksDetail | null | undefined;
  isPdfMode?: boolean;
};

const CHECK_PAGE_SIZE = 10;

const borderTeal = theme?.colors?.primary?.border?.regular ?? '#03BAA1';
const headerBg = colors.neutral.bg.regular;
const cellBorder = colors.neutral.border.weaker;
const bodyText = colors.neutral.text.strong;
const riskRed = colors.danger.text.regular;
const riskRedMuted = colors.danger.text.weak;
const labelMuted = colors.neutral.text.muted;

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  borderBottom: `1px solid ${cellBorder}`,
  color: bodyText,
};

const pjMontoDivider: React.CSSProperties = {
  borderLeft: `1px solid ${cellBorder}`,
};

const pjThGroup: React.CSSProperties = {
  padding: '10px 12px',
  background: headerBg,
  textAlign: 'center',
  fontWeight: 700,
  fontSize: 14,
  color: bodyText,
  borderBottom: `1px solid ${cellBorder}`,
};

const pjThGroupCorner: React.CSSProperties = {
  ...pjThGroup,
  textAlign: 'left',
};

const pjThBand: React.CSSProperties = {
  padding: '10px 12px',
  background: colors.neutral.bg.inverted,
  textAlign: 'center',
  fontWeight: 700,
  fontSize: 13,
  color: bodyText,
  borderBottom: `1px solid ${cellBorder}`,
};

const pjThBandCorner: React.CSSProperties = {
  padding: '10px 12px',
  background: colors.neutral.bg.inverted,
  borderBottom: `1px solid ${cellBorder}`,
};

function pjDataCellStyle(
  value: string,
  options: { emphasize?: boolean; bold?: boolean; divider?: boolean } = {}
): React.CSSProperties {
  const highlight = options.emphasize && value !== '-';
  return {
    ...tdStyle,
    textAlign: 'center',
    fontWeight: options.bold ? 700 : 400,
    color: highlight ? riskRed : bodyText,
    ...(options.divider ? pjMontoDivider : {}),
  };
}

const NosisRejectedChecksSection: React.FC<Props> = ({
  detail,
  isPdfMode = false,
}) => {
  const blocks = buildAntecedentesBlocks(detail);
  const detailStatus = detail?.detailStatus;
  const pjStatus = detail?.pjStatus;
  const pj = detail?.pj;
  const pjRows = ensurePjReasonRows(pj?.rows);
  const checkRows = mapCheckRowsForTable(detail?.checks);
  const monthlyRows = mapMonthlyRows(detail?.monthlyEvolution);
  const monthlyTotal = buildMonthlyTotalActual(detail?.monthlyEvolution);
  const fineRows = formatFineRow(detail?.finePayments);
  const redMetrics = buildRedNosisMetrics(detail?.redNosis);

  const [checkPage, setCheckPage] = useState(0);
  useEffect(() => {
    setCheckPage(0);
  }, [detail?.checks]);

  const checkPageSize = isPdfMode
    ? Math.max(checkRows.length, 1)
    : CHECK_PAGE_SIZE;
  const checkTotalPages = Math.max(
    1,
    Math.ceil(checkRows.length / checkPageSize) || 1
  );
  const pagedCheckRows = useMemo(() => {
    if (isPdfMode) return checkRows;
    const start = checkPage * CHECK_PAGE_SIZE;
    return checkRows.slice(start, start + CHECK_PAGE_SIZE);
  }, [checkRows, checkPage, isPdfMode]);

  const handleCheckChangePage = (page: number) => {
    if (page < 0 || page >= checkTotalPages) return;
    setCheckPage(page);
  };

  const showPjEmpty = pjStatus === 'NO_REJECTIONS';
  const showChecksEmpty =
    detailStatus === 'NO_REJECTIONS' ||
    (detailStatus === 'REJECTED' && checkRows.length === 0);

  const checkColumns = [
    {
      name: 'Fecha rechazo',
      selector: (row: (typeof checkRows)[0]) => (
        <Span type='3' variant='neutral'>
          {row.rejectionDate}
        </Span>
      ),
      grow: 1.2,
    },
    {
      name: 'N°',
      selector: (row: (typeof checkRows)[0]) => (
        <Span type='3' variant='neutral'>
          {row.checkNumber}
        </Span>
      ),
      grow: 0.7,
    },
    {
      name: 'Monto',
      selector: (row: (typeof checkRows)[0]) => (
        <Span type='3' variant='neutral'>
          {row.amount}
        </Span>
      ),
      grow: 1.2,
    },
    {
      name: 'Causal',
      selector: (row: (typeof checkRows)[0]) => (
        <Span type='3' variant='neutral'>
          {row.reason}
        </Span>
      ),
      grow: 1.2,
    },
    {
      name: 'Estado del cheque',
      selector: (row: (typeof checkRows)[0]) =>
        row.statusKind === 'impago' ? (
          <Badge variant='danger' type='text' size='md'>
            {row.statusLabel}
          </Badge>
        ) : (
          <Span type='3' variant='neutral'>
            {row.statusLabel}
          </Span>
        ),
      grow: 1.2,
    },
    {
      name: 'Días',
      selector: (row: (typeof checkRows)[0]) => (
        <Span type='3' variant='neutral'>
          {row.daysToPayment}
        </Span>
      ),
      grow: 0.6,
    },
    {
      name: 'Multa',
      selector: (row: (typeof checkRows)[0]) => (
        <Span type='3' variant='neutral'>
          {row.fine}
        </Span>
      ),
      grow: 1.1,
    },
    {
      name: 'CUIT relacionado',
      selector: (row: (typeof checkRows)[0]) => (
        <Span type='3' variant='neutral'>
          {row.relatedCuit}
        </Span>
      ),
      grow: 1.4,
    },
  ];

  const renderPjTable = () => {
    const bands = PJ_BAND_KEYS as PjBandKey[];
    const firstMontoBand = bands[0];

    return (
      <table
        data-testid='nosis-pj-age-table'
        style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}
      >
        <thead>
          <tr>
            <th style={pjThGroupCorner} aria-hidden='true' />
            <th style={pjThGroup} colSpan={bands.length}>
              Cantidad
            </th>
            <th
              style={{ ...pjThGroup, ...pjMontoDivider }}
              colSpan={bands.length}
            >
              Monto
            </th>
          </tr>
          <tr>
            <th style={pjThBandCorner} aria-hidden='true' />
            {bands.map((b) => (
              <th key={`c-${b}`} style={pjThBand}>
                {PJ_BAND_LABELS[b]}
              </th>
            ))}
            {bands.map((b) => (
              <th
                key={`m-${b}`}
                style={{
                  ...pjThBand,
                  ...(b === firstMontoBand ? pjMontoDivider : {}),
                }}
              >
                {PJ_BAND_LABELS[b]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pjRows.map((row) => {
            const label = formatReasonLabel(row.reason);
            const isInsufficientFunds = /sin fondos/i.test(label);
            return (
              <tr key={row.reason}>
                <td style={tdStyle}>{label}</td>
                {bands.map((b) => {
                  const value = pjBandCell(pj, row.reason, b, 'count');
                  return (
                    <td
                      key={`${row.reason}-c-${b}`}
                      style={pjDataCellStyle(value, {
                        emphasize: isInsufficientFunds,
                      })}
                    >
                      {value}
                    </td>
                  );
                })}
                {bands.map((b) => {
                  const value = pjBandCell(pj, row.reason, b, 'amount');
                  return (
                    <td
                      key={`${row.reason}-m-${b}`}
                      style={pjDataCellStyle(value, {
                        emphasize: isInsufficientFunds,
                        divider: b === firstMontoBand,
                      })}
                    >
                      {value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr>
            <td style={{ ...tdStyle, fontWeight: 700 }}>Total rechazados</td>
            {bands.map((b) => {
              const value = pjTotalsCell(pj, b, 'rejected', 'count');
              return (
                <td
                  key={`tr-c-${b}`}
                  style={pjDataCellStyle(value, {
                    emphasize: true,
                    bold: true,
                  })}
                >
                  {value}
                </td>
              );
            })}
            {bands.map((b) => {
              const value = pjTotalsCell(pj, b, 'rejected', 'amount');
              return (
                <td
                  key={`tr-m-${b}`}
                  style={pjDataCellStyle(value, {
                    emphasize: true,
                    bold: true,
                    divider: b === firstMontoBand,
                  })}
                >
                  {value}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={tdStyle}>Total abonados</td>
            {bands.map((b) => {
              const value = pjTotalsCell(pj, b, 'paid', 'count');
              return (
                <td key={`tp-c-${b}`} style={pjDataCellStyle(value)}>
                  {value}
                </td>
              );
            })}
            {bands.map((b) => {
              const value = pjTotalsCell(pj, b, 'paid', 'amount');
              return (
                <td
                  key={`tp-m-${b}`}
                  style={pjDataCellStyle(value, {
                    divider: b === firstMontoBand,
                  })}
                >
                  {value}
                </td>
              );
            })}
          </tr>
          <tr>
            <td style={tdStyle}>% abonados</td>
            {bands.map((b) => {
              const value = pjTotalsCell(pj, b, 'pct', 'count');
              return (
                <td
                  key={`pct-c-${b}`}
                  style={pjDataCellStyle(value, { emphasize: true })}
                >
                  {value}
                </td>
              );
            })}
            {bands.map((b) => {
              const value = pjTotalsCell(pj, b, 'pct', 'amount');
              return (
                <td
                  key={`pct-m-${b}`}
                  style={pjDataCellStyle(value, {
                    emphasize: true,
                    divider: b === firstMontoBand,
                  })}
                >
                  {value}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <div data-testid='nosis-rejected-checks-section'>
      <Card margin={{ top: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
          style={{ marginBottom: 12 }}
        >
          Cheques
        </P>
        <P
          type='3'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
          style={{ marginBottom: 24 }}
        >
          Antecedentes de cheques
        </P>

        <div data-testid='nosis-antecedentes-summary'>
          <Card
            display='grid'
            gap='24px'
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              alignItems: 'flex-start',
              rowGap: '8px',
              marginBottom: 16,
            }}
          >
            {blocks.map((block) => {
              const labelColor = block.emphasize ? riskRed : bodyText;
              const valueColor = block.emphasize ? riskRed : bodyText;
              const secondaryColor = block.emphasize ? riskRedMuted : bodyText;

              return (
                <div
                  key={block.id}
                  data-testid={`nosis-antecedentes-${block.id}`}
                >
                  <P
                    type='3'
                    variant='neutral-1'
                    withMargin={false}
                    weight='bold'
                    modifier='strong'
                    style={{ color: labelColor }}
                  >
                    {block.titleLabel}
                    {block.titleEmphasis ? (
                      <>
                        {' '}
                        <span style={{ fontWeight: 700 }}>
                          {block.titleEmphasis}
                        </span>
                      </>
                    ) : null}
                  </P>
                  <H
                    type='3'
                    variant='neutral-1'
                    weight='bold'
                    withMargin={false}
                    style={{
                      marginTop: 8,
                      color: valueColor,
                      fontSize: block.emphasize ? 24 : 20,
                      lineHeight: block.emphasize ? '32px' : '28px',
                    }}
                  >
                    {block.primaryValue}
                  </H>
                  {block.secondaryLine ? (
                    <P
                      type='4'
                      variant='neutral-1'
                      withMargin={false}
                      weight='bold'
                      modifier='strong'
                      style={{ color: secondaryColor, marginTop: 8 }}
                    >
                      {block.secondaryLine}
                    </P>
                  ) : null}
                </div>
              );
            })}
          </Card>
        </div>

        <Collapsable
          id='nosis-rejected-checks-detail'
          title={(open) => (open ? 'Ocultar detalle' : 'Ver detalle')}
          titleVariant='primary'
          titleModifier='strong'
          iconVariant='primary'
          startOpened={isPdfMode}
          forceOpen={isPdfMode}
          hideToggle={isPdfMode}
          headerStyle={(open) =>
            open
              ? {
                  borderBottom: `1px solid ${borderTeal}`,
                  borderRadius: 0,
                  paddingBottom: '6px',
                }
              : {
                  border: `1px solid ${borderTeal}`,
                  borderRadius: '6px',
                  padding: '6px 16px',
                }
          }
        >
          <div data-testid='nosis-rejected-checks-detail-body'>
            <Card margin={{ top: '3' }}>
              <P
                type='3'
                variant='neutral-1'
                withMargin={false}
                weight='bold'
                modifier='strong'
              >
                Rechazos por antigüedad - personas jurídicas
              </P>
              {pjStatus === 'NO_DATA' ? (
                <P type='3' variant='neutral' modifier='weak'>
                  -
                </P>
              ) : showPjEmpty ? (
                <div data-testid='nosis-pj-empty'>
                  <P type='3' variant='neutral' modifier='weak'>
                    Sin rechazos por antigüedad
                  </P>
                </div>
              ) : (
                renderPjTable()
              )}

              <Card margin={{ top: '4' }}>
                <P
                  type='3'
                  variant='neutral-1'
                  withMargin={false}
                  weight='bold'
                  modifier='strong'
                >
                  Detalle de rechazos (últimos 30 cheques)
                </P>
                {detailStatus === 'NO_DATA' ? (
                  <P type='3' variant='neutral' modifier='weak'>
                    -
                  </P>
                ) : showChecksEmpty ? (
                  <div data-testid='nosis-checks-empty'>
                    <P type='3' variant='neutral' modifier='weak'>
                      Sin cheques rechazados
                    </P>
                  </div>
                ) : (
                  <Table
                    columns={checkColumns}
                    data={pagedCheckRows}
                    tableData={{
                      page: checkPage,
                      size: checkPageSize,
                      totalElements: checkRows.length,
                      totalPages: checkTotalPages,
                    }}
                    handleChangePage={handleCheckChangePage}
                    variant='weak'
                    disablePagination={isPdfMode}
                    keyField='id'
                  />
                )}
              </Card>

              <Card margin={{ top: '4' }}>
                <P
                  type='3'
                  variant='neutral-1'
                  withMargin={false}
                  weight='bold'
                  modifier='strong'
                >
                  Evolución mensual
                </P>
                <P
                  type='2'
                  variant='neutral-1'
                  modifier='weak'
                  withMargin={false}
                >
                  Basado en los últimos 30 cheques
                </P>
                {detailStatus === 'NO_DATA' ? (
                  <P type='3' variant='neutral' modifier='weak'>
                    -
                  </P>
                ) : (
                  <table
                    data-testid='nosis-monthly-evolution-table'
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      marginTop: 8,
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={pjThGroupCorner} aria-hidden='true' />
                        <th style={pjThGroup} colSpan={3}>
                          Cantidad de cheques
                        </th>
                        <th
                          style={{ ...pjThGroup, ...pjMontoDivider }}
                          colSpan={3}
                        >
                          Monto
                        </th>
                      </tr>
                      <tr>
                        <th style={{ ...pjThBand, textAlign: 'left' }}>
                          Período
                        </th>
                        {['Rechazados', 'Abonados', 'Pend. acum.'].map((h) => (
                          <th key={`qc-${h}`} style={pjThBand}>
                            {h}
                          </th>
                        ))}
                        {['Rechazados', 'Abonados', 'Pend. acum.'].map(
                          (h, index) => (
                            <th
                              key={`qm-${h}`}
                              style={{
                                ...pjThBand,
                                ...(index === 0 ? pjMontoDivider : {}),
                              }}
                            >
                              {h}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyRows.map((row) => (
                        <tr key={row.id}>
                          <td style={tdStyle}>{row.periodo}</td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {row.rejCant}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {row.pagCant}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {row.pendCant}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'center',
                              ...pjMontoDivider,
                            }}
                          >
                            {row.rejMonto}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'center' }}>
                            {row.pagMonto}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'center',
                              color:
                                row.pendMonto !== '-' ? riskRed : tdStyle.color,
                            }}
                          >
                            {row.pendMonto}
                          </td>
                        </tr>
                      ))}
                      <tr data-testid='nosis-monthly-total-actual'>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>
                          {monthlyTotal.periodo}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'center',
                            fontWeight: 700,
                          }}
                        >
                          {monthlyTotal.rejCant}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'center',
                            fontWeight: 700,
                          }}
                        >
                          {monthlyTotal.pagCant}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'center',
                            fontWeight: 700,
                          }}
                        >
                          {monthlyTotal.pendCant}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'center',
                            fontWeight: 700,
                            ...pjMontoDivider,
                          }}
                        >
                          {monthlyTotal.rejMonto}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'center',
                            fontWeight: 700,
                          }}
                        >
                          {monthlyTotal.pagMonto}
                        </td>
                        <td
                          style={{
                            ...tdStyle,
                            textAlign: 'center',
                            fontWeight: 700,
                            color:
                              monthlyTotal.pendMonto !== '-'
                                ? riskRed
                                : tdStyle.color,
                          }}
                        >
                          {monthlyTotal.pendMonto}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </Card>

              <Card margin={{ top: '4' }}>
                <P
                  type='3'
                  variant='neutral-1'
                  withMargin={false}
                  weight='bold'
                  modifier='strong'
                >
                  Pagos de multa
                </P>
                <Table
                  columns={[
                    {
                      name: 'Estado',
                      selector: (row: (typeof fineRows)[0]) => (
                        <Span type='3' variant='neutral'>
                          {row.estado}
                        </Span>
                      ),
                    },
                    {
                      name: 'Cantidad',
                      selector: (row: (typeof fineRows)[0]) => (
                        <Span type='3' variant='neutral'>
                          {row.cantidad}
                        </Span>
                      ),
                    },
                    {
                      name: 'Desde',
                      selector: (row: (typeof fineRows)[0]) => (
                        <Span type='3' variant='neutral'>
                          {row.desde}
                        </Span>
                      ),
                    },
                    {
                      name: 'Hasta',
                      selector: (row: (typeof fineRows)[0]) => (
                        <Span type='3' variant='neutral'>
                          {row.hasta}
                        </Span>
                      ),
                    },
                  ]}
                  data={fineRows}
                  tableData={{ size: 10, totalElements: fineRows.length }}
                  variant='weak'
                  disablePagination
                  keyField='estado'
                />
              </Card>

              <div data-testid='nosis-red-nosis'>
                <Card margin={{ top: '4' }}>
                  <P
                    type='3'
                    variant='neutral-1'
                    withMargin={false}
                    weight='bold'
                    modifier='strong'
                    style={{ marginBottom: 12 }}
                  >
                    Informados por la red Nosis
                  </P>
                  <Card
                    display='grid'
                    gap='24px'
                    borderRadius={false}
                    style={{
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(140px, 1fr))',
                      alignItems: 'flex-start',
                      paddingBottom: 12,
                      borderBottom: `1px solid ${borderTeal}`,
                      borderRadius: 0,
                    }}
                  >
                    {redMetrics.map((m) => (
                      <div key={m.id} style={{ textAlign: 'left' }}>
                        {m.periodLabel ? (
                          <P
                            type='4'
                            variant='neutral-1'
                            withMargin={false}
                            weight='bold'
                            modifier='strong'
                            style={{ color: labelMuted, textAlign: 'left' }}
                          >
                            {m.periodLabel}
                          </P>
                        ) : (
                          <P
                            type='4'
                            withMargin={false}
                            aria-hidden
                            style={{ visibility: 'hidden' }}
                          >
                            &nbsp;
                          </P>
                        )}
                        <P
                          type='3'
                          variant='neutral-1'
                          withMargin={false}
                          weight='bold'
                          modifier='strong'
                          style={{ marginTop: 4, textAlign: 'left' }}
                        >
                          {m.titleLabel}
                        </P>
                        <H
                          type='3'
                          variant='neutral-1'
                          weight='bold'
                          withMargin={false}
                          style={{ marginTop: 8, textAlign: 'left' }}
                        >
                          {m.primary}
                        </H>
                        {m.secondary != null && (
                          <P
                            type='4'
                            withMargin={false}
                            variant='neutral-1'
                            weight='bold'
                            modifier='strong'
                            style={{
                              color: labelMuted,
                              marginTop: 8,
                              textAlign: 'left',
                            }}
                          >
                            {m.secondary}
                          </P>
                        )}
                      </div>
                    ))}
                  </Card>
                </Card>
              </div>
            </Card>
          </div>
        </Collapsable>
      </Card>
    </div>
  );
};

export default NosisRejectedChecksSection;
